// Model capability table, prices and cost from usage (research-claude-api §2, §3; moved from the SDK skeleton `models.ts`).
// Pure functions only. `UsageLike` is a structural subset of the SDK's BetaUsage so this file has no SDK dependency
// (the web bundle imports @hq/shared too); BetaUsage is assignable to it.
import type { Effort, ModelId, SegmentUse } from './domain.ts';

/** Static per-model facts (research-claude-api §2). Renamed from the SDK skeleton's `ModelCaps`: that name is now the §7.2 flag pair. */
export interface ModelInfo {
  id: ModelId;
  label: string;                       // settings dropdown (German UI)
  contextWindow: number;
  maxOutput: number;
  /** adaptive thinking + effort + display:"updates" + role:"system" messages + per-message effort */
  family5x: boolean;
  defaultEffortApi: Effort | null;     // what the API does when effort is omitted
  serverFallback: boolean;             // fallbacks:"default"
  onDemandCompaction: boolean;         // compact-2026-09-04
  minCacheablePrefix: number;
  requires30DayRetention: boolean;     // Fable 5.1: 400 under ZDR
}

export const MODELS: Record<ModelId, ModelInfo> = {
  'claude-opus-5-5': {
    id: 'claude-opus-5-5', label: 'Opus 5.5 (Standard)', contextWindow: 1_000_000, maxOutput: 128_000,
    family5x: true, defaultEffortApi: 'medium', serverFallback: true, onDemandCompaction: true,
    minCacheablePrefix: 512, requires30DayRetention: false,
  },
  'claude-sonnet-5-5': {
    id: 'claude-sonnet-5-5', label: 'Sonnet 5.5 (schnell, günstiger)', contextWindow: 1_000_000, maxOutput: 128_000,
    family5x: true, defaultEffortApi: 'high', serverFallback: true, onDemandCompaction: true,
    minCacheablePrefix: 512, requires30DayRetention: false,
  },
  'claude-fable-5-1': {
    id: 'claude-fable-5-1', label: 'Fable 5.1 (Spitze, teuer)', contextWindow: 1_000_000, maxOutput: 128_000,
    family5x: true, defaultEffortApi: 'high', serverFallback: true, onDemandCompaction: true,
    minCacheablePrefix: 512, requires30DayRetention: true,
  },
  'claude-haiku-4-5': {
    id: 'claude-haiku-4-5', label: 'Haiku 4.5 (Kleinkram)', contextWindow: 200_000, maxOutput: 64_000,
    family5x: false, defaultEffortApi: null, serverFallback: false, onDemandCompaction: false,
    minCacheablePrefix: 4096, requires30DayRetention: false,
  },
};

export const MODEL_IDS: readonly ModelId[] = ['claude-opus-5-5', 'claude-sonnet-5-5', 'claude-fable-5-1', 'claude-haiku-4-5'];

/** Dropdown entries in display order (research-claude-api §2.1). */
export const MODEL_OPTIONS: ReadonlyArray<{ id: ModelId; label: string }> = MODEL_IDS.map((id) => ({ id, label: MODELS[id].label }));

export function isModelId(v: unknown): v is ModelId {
  return typeof v === 'string' && (MODEL_IDS as readonly string[]).includes(v);
}

/** USD per million tokens. cacheRead/cacheWrite are absolute rates, not multipliers. */
export interface Price { input: number; output: number; write5m: number; write1h: number; read: number }

// Matched by prefix, longest first, so dated ids resolve too. Fallback targets are listed because a
// refusal fallback can bill them (usage.iterations).
const PRICES: Array<[prefix: string, price: Price]> = [
  ['claude-opus-5-5', { input: 4, output: 20, write5m: 5, write1h: 8, read: 0.2 }],
  ['claude-sonnet-5-5', { input: 2, output: 10, write5m: 2.5, write1h: 4, read: 0.2 }],
  ['claude-fable-5-1', { input: 10, output: 50, write5m: 12.5, write1h: 20, read: 0.25 }],
  ['claude-haiku-4-5', { input: 1, output: 5, write5m: 1.25, write1h: 2, read: 0.1 }],
  ['claude-opus-5', { input: 5, output: 25, write5m: 6.25, write1h: 10, read: 0.5 }],
  ['claude-opus-4-8', { input: 5, output: 25, write5m: 6.25, write1h: 10, read: 0.5 }],
  ['claude-sonnet-5', { input: 2, output: 10, write5m: 2.5, write1h: 4, read: 0.2 }],
];
const PRICES_LONGEST_FIRST = [...PRICES].sort((a, b) => b[0].length - a[0].length);
export const WEB_SEARCH_USD_PER_REQUEST = 0.01; // $10 per 1,000 searches; web_fetch has no fee

export function priceFor(model: string): Price | null {
  const hit = PRICES_LONGEST_FIRST.find(([p]) => model.startsWith(p));
  return hit ? hit[1] : null;
}

export interface TokenCounts {
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens: number | null;
  cache_creation_input_tokens: number | null;
  cache_creation: { ephemeral_5m_input_tokens: number; ephemeral_1h_input_tokens: number } | null;
}

/** Structural subset of the SDK's `BetaUsage`. */
export interface UsageLike extends TokenCounts {
  inference_geo?: string | null;
  iterations?: Array<TokenCounts & { type: string; model?: string | null }> | null;
  server_tool_use?: { web_search_requests: number } | null;
}

function tokensUsd(t: TokenCounts, p: Price): number {
  const w1h = t.cache_creation?.ephemeral_1h_input_tokens ?? 0;
  // If the split is missing, treat all cache writes as 5m.
  const w5m = t.cache_creation ? t.cache_creation.ephemeral_5m_input_tokens : (t.cache_creation_input_tokens ?? 0);
  return (
    t.input_tokens * p.input +
    w5m * p.write5m +
    w1h * p.write1h +
    (t.cache_read_input_tokens ?? 0) * p.read +
    t.output_tokens * p.output
  ) / 1_000_000;
}

export interface Cost { usd: number; unknownModels: string[]; lines: Array<{ model: string; kind: string; usd: number }> }

/**
 * Cost of ONE API response (research-claude-api §2.2).
 * - usage.iterations present (fallback, compaction, advisor): sum every entry, each at its own model's price.
 * - otherwise: top-level usage at the response model's price.
 * requestModel prices entries that carry no model (compaction).
 */
export function costOf(usage: UsageLike, responseModel: string, requestModel: string): Cost {
  const lines: Cost['lines'] = [];
  const unknownModels: string[] = [];
  const geo = usage.inference_geo === 'us' ? 1.1 : 1; // we send no inference_geo, so this stays 1
  const add = (model: string, kind: string, t: TokenCounts) => {
    const p = priceFor(model);
    if (!p) { unknownModels.push(model); return; }
    lines.push({ model, kind, usd: tokensUsd(t, p) * geo });
  };
  if (usage.iterations && usage.iterations.length > 0) {
    for (const it of usage.iterations) add(it.model ? it.model : requestModel, it.type, it);
  } else {
    add(responseModel, 'message', usage);
  }
  const searches = usage.server_tool_use?.web_search_requests ?? 0;
  if (searches > 0) lines.push({ model: 'web_search', kind: 'server_tool', usd: searches * WEB_SEARCH_USD_PER_REQUEST });
  return { usd: lines.reduce((s, l) => s + l.usd, 0), unknownModels, lines };
}

/** Effort per use. Haiku has no effort parameter (null = do not send output_config.effort). */
export type Use = 'chat' | 'background' | 'voice' | 'deep' | 'utility';
export function effortFor(model: ModelId, use: Use): Effort | null {
  if (!MODELS[model].family5x) return null;
  const table: Record<Use, Effort> =
    model === 'claude-sonnet-5-5'
      ? { chat: 'low', background: 'medium', voice: 'low', deep: 'high', utility: 'low' }
      : { chat: 'medium', background: 'medium', voice: 'low', deep: 'high', utility: 'low' }; // Opus 5.5, Fable 5.1
  return table[use];
}

export const MAX_TOKENS: Record<Use, number> = { chat: 32_000, background: 64_000, voice: 4_096, deep: 64_000, utility: 2_048 };

/** The one mapping from segment use to research use (§7.2). Never index effortFor/MAX_TOKENS with a SegmentUse. */
export const USE_OF: Record<SegmentUse, Use> = { chat: 'chat', meeting: 'chat', job: 'background', task: 'background', voice: 'voice' };
